import { Component, HostListener, OnInit } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { LANGUAGES } from './shared/languages/language';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from './shared/services/data.service';
import { SharedRequesterService } from './shared/services/shared-requester.service';
import { MODULESUBTITLE } from './shared/moduleList/moduleSubList';
import { filter, pairwise, take } from 'rxjs/operators';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { Logger, CryptoUtils } from 'msal';
import { AuthService } from './core/adal/auth.service';
import { RequesterService } from './features/configurator/services/requester.service';
import { ROLES } from './shared/roleList/priorityRoleList';
import { NavigationStart, NavigationEnd } from '@angular/router';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  title = 'unilever-spc';
  languageMasterList = LANGUAGES;
  selectedLanguage: any;
  isLoggedIn!: boolean;
  isIframe!: boolean;
  username: any;
  userRole: any;
  Roles: any[] = ROLES;
  isRoleAvailable: boolean = true;
  isMsalReady: boolean = false;  // ← ADD THIS - Track MSAL initialization
  isLoginRedirecting: boolean = false;  // ← Prevent multiple redirect calls

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler(event): void {
    const urlSplit = this.router.url.split('/');
    const name = [...MODULESUBTITLE].find(item => item.link === urlSplit[urlSplit.length - 1]);
    if (name) {
      this.sharedRequester.userAnalytics({ screenName: name.userAnalytics });
    }
    this.setLastTabData();
  }

  constructor(
    // private adalService: AdalService,
    private router: Router,
    private translate: TranslateService,
    private dataSer: DataService,
    private authService: AuthService,
    private sharedRequester: SharedRequesterService,
    private MsalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private requester: RequesterService,
  ) {
    sessionStorage.setItem('isLogout', 'false');
    // localStorage.removeItem('userRole');
    if (this.router.events) {
      this.router.events
        .pipe(filter((evt: any) => evt instanceof RoutesRecognized), pairwise())
        .subscribe((events: RoutesRecognized[]) => {
          const preUrl = events && events[0] ? events[0].urlAfterRedirects : '';
          const curUrl = events && events[1] ? events[1].urlAfterRedirects : '';
          const spl = curUrl ? curUrl.split('/') : [];
          const curl = spl[spl.length - 1] ? spl[spl.length - 1] : '';
          this.sharedRequester.isFirstTimeAppLoading = (preUrl && (preUrl.trim() === '/' || preUrl.trim() === '/location-picker') && curl === 'dashboard');
          if (!localStorage.getItem('dfosLines')) {
            setTimeout(() => {
              this.getDfosLine();
            }, 1000);

          }
        });

      // Detect when guard blocks navigation due to no account
      this.router.events
        .pipe(filter((evt: any) => evt instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
        });

      // Detect when navigation ends (component loaded) vs blocked (component not loaded)
      this.router.events
        .pipe(filter((evt: any) => evt instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
        });
    }
  }

  ngOnInit() {
    this.username = localStorage.getItem('username') || '';
    this.userRole = localStorage.getItem('userRole') || '';
    this.isIframe = window !== window.parent && !window.opener;

    // Check for account IMMEDIATELY - handles already-logged-in users
    const immediateAccount = this.msalService.instance.getActiveAccount() ||
      this.msalService.instance.getAllAccounts()?.[0];

    if (immediateAccount) {
      // Account exists immediately, proceed with normal flow
      this.isMsalReady = true;
      this.handleAuthAfterMsalInit();
    } else {
      // No account immediately - wait for MSAL to process auth code (from Azure redirect)

      let msalResponseHandled = false;

      this.MsalBroadcastService.inProgress$
        .pipe(
          filter((status) => status === InteractionStatus.None),
          take(1)
        )
        .subscribe(() => {
          if (msalResponseHandled) {
            return;
          }
          msalResponseHandled = true;


          // Check for account AFTER MSAL finishes processing auth code
          const accountAfterMsal = this.msalService.instance.getActiveAccount() ||
            this.msalService.instance.getAllAccounts()?.[0];

          if (accountAfterMsal) {
            // Account found after MSAL processing
            this.isMsalReady = true;
            this.handleAuthAfterMsalInit();
          } else {
            // Still no account - user needs to login
            this.isMsalReady = true;  // Mark as ready so we don't show loader
            this.login();
          }
        });

      // FALLBACK: If MSAL doesn't respond within 3 seconds (e.g., already finished startup),
      // assume ready and check account. This handles the race condition where MSAL 
      // is already at InteractionStatus.None before we subscribe.
      setTimeout(() => {
        if (!msalResponseHandled && !this.isMsalReady) {
          msalResponseHandled = true;
          this.isMsalReady = true;  // Hide loader immediately

          const accountAfterTimeout = this.msalService.instance.getActiveAccount() ||
            this.msalService.instance.getAllAccounts()?.[0];

          if (accountAfterTimeout) {
            this.handleAuthAfterMsalInit();
          } else {
            this.login();  // This will call loginRedirect()
          }
        } else if (msalResponseHandled) {
          console.log('%c[AppComponent] Timeout fired but MSAL subscription already handled', 'color: #74c0fc;');
        } else if (this.isMsalReady) {
        }
      }, 3000);
    }

    // language translation
    this.initializeTranslations();
  }

  private initializeTranslations() {
    let flag = false;
    const selectedFactory = JSON.parse(sessionStorage.getItem('selectedFactory'));

    const languageMaster = sessionStorage.getItem('languageMaster');
    this.selectedLanguage = languageMaster ? JSON.parse(languageMaster) : LANGUAGES[0];

    const usl_lsl = sessionStorage.getItem('usl_lsl');
    const selectedUsllsl = usl_lsl ? JSON.parse(usl_lsl) : true;

    const redZoneFilter = sessionStorage.getItem('redZoneFilter');
    const selecteRedzone = redZoneFilter ? JSON.parse(redZoneFilter) : false;

    if (selectedFactory && !this.selectedLanguage) {
      for (const lang of this.languageMasterList) {
        if (lang.language === selectedFactory.language) {
          flag = true;
          this.selectedLanguage = lang;
        }
      }
      if (!flag) {
        this.selectedLanguage = LANGUAGES[0];
      }
    }
    // this.translate.use(this.selectedLanguage.code);

    // Register all available languages
    const languageCodes = this.languageMasterList.map(lang => lang.code);
    this.translate.addLangs(languageCodes);
    this.translate.setDefaultLang(LANGUAGES[0].code);

    // Use the selected language
    this.translate.use(this.selectedLanguage.code).subscribe(
      () => console.log(`Language ${this.selectedLanguage.code} loaded successfully`),
      (error) => console.error(`Failed to load language ${this.selectedLanguage.code}`, error)
    );
    sessionStorage.setItem('languageMaster', JSON.stringify(this.selectedLanguage));
    sessionStorage.setItem('usl_lsl', JSON.stringify(selectedUsllsl));
    sessionStorage.setItem('redZoneFilter', JSON.stringify(selecteRedzone));
  }

  private handleAuthAfterMsalInit() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();
    const account = activeAccount || (accounts.length > 0 ? accounts[0] : null);

    if (account) {
      this.msalService.instance.setActiveAccount(account);
      this.redirectTo(account);
      return;
    }

    this.login();
  }

  checkoutAccount() {
    this.isLoggedIn = !!this.msalService.instance.getActiveAccount();
  }

  setLastTabData() {
    if (sessionStorage.getItem('selectedFactory') && sessionStorage.getItem('selectedCluster')) {
      localStorage.setItem('lastSelectedFactory', sessionStorage.getItem('selectedFactory'));
      localStorage.setItem('lastSelectedCluster', sessionStorage.getItem('selectedCluster'));
    }
  }

  login() {

    let account = this.msalService.instance.getActiveAccount();

    if (!account) {
      const allAccounts = this.msalService.instance.getAllAccounts();
      if (allAccounts.length > 0) {
        account = allAccounts[0];
        this.msalService.instance.setActiveAccount(account);
      }
    }

    if (account) {
      const lowerCaseArray = [];
      const roles = account.idTokenClaims?.roles || [];
      for (let i = 0; i < roles.length; i++) {
        lowerCaseArray.push(roles[i].toLowerCase());
      }
      this.isRoleAvailable = this.Roles.some(value => lowerCaseArray.includes(value));

      if (!this.isRoleAvailable) {
        this.router.navigate(['/no-role']);
        return;
      }
      this.checkoutADAccount();
      sessionStorage.setItem('loginRedirected', 'false');
      this.redirectTo(account);
      return;
    } else {
      // No account found - redirect to MSAL login
      // Guard against multiple concurrent redirect calls
      if (this.isLoginRedirecting) {
        return;
      }

      // Mark that we're redirecting BEFORE calling loginRedirect()
      this.isLoginRedirecting = true;

      try {
        this.msalService.loginRedirect();
        // If we reach here, loginRedirect() was queued (it's async)
      } catch (error) {
        this.isLoginRedirecting = false;
        // Fallback: try navigating to login (shouldn't reach here in normal flow)
        this.router.navigate(['/']);
      }
    }
  }

  public logout(): void {
    localStorage.removeItem('userRole');
    this.msalService.logoutRedirect();
  }

  public async checkoutADAccount() {
    const loggedIn = !!this.msalService.instance.getActiveAccount();
    if (loggedIn) {
      // To renew idToken, please pass clientId as the only scope
      const accessTokenRequest = {
        scopes: ['user.read', 'mail.send'],
        account: this.msalService.instance.getActiveAccount()
        // scopes: [environment.msalAuthConfig.auth.clientId],
        // authority: environment.msalAuthConfig.auth.authority
      };

      // this.msalService.acquireTokenSilent(accessTokenRequest).then(async (payload: any) => {
      this.msalService.acquireTokenSilent(accessTokenRequest).subscribe({
        next: (payload: any) => {
          if (payload && payload.account && payload.account.name) { // Assign username
            this.username = payload.account.name;
          }
          if (payload && payload.idToken) {

            localStorage.setItem('graphToken', payload.idToken);
          }
          // this.authService.setTokenExpiryTime(payload); // Set token expiry time
          // Set token expiry time when acquired
          if (payload && payload.expiresOn) {
            this.authService.setTokenExpiryTime(payload);
          }
        },
        error: (error) => {
          console.error('Token acquisition error in checkoutADAccount:', error);
          // Fallback: trigger interactive login if silent token acquisition fails
          if (error.errorCode === 'interaction_required' ||
            error.errorCode === 'consent_required') {
            this.msalService.loginRedirect();
          }
        }
      });
    }
  }

  public redirectTo(account: any): void {
    if (!account) {
      return;
    }
    if (account.idTokenClaims.roles.length === 0) {
      this.router.navigate(['/no-role']);
    } else {
      const profile = account.idTokenClaims.roles ? account.idTokenClaims.roles : [];
      const priority = this.dataSer.rolePrioritySetter(profile);
      const currentRole = priority.length ? priority[0] : account.idTokenClaims.roles[0];
      const storedRole = localStorage.getItem('userRole') ? localStorage.getItem('userRole') : '';

      // CRITICAL FIX: Only navigate if role actually changed (not just missing from storage)
      // If userRole is empty but account has role, update storage WITHOUT navigating
      if (!storedRole && currentRole) {
        localStorage.setItem('userRole', currentRole);
        // Don't navigate - user is already where they want to be
      } else if (currentRole !== storedRole) {
        // Role actually changed (different role, not just missing) - clear and redirect
        localStorage.removeItem('lastSelectedFactory');
        localStorage.removeItem('lastSelectedCluster');
        localStorage.removeItem('savedClusterList');
        localStorage.setItem('userRole', currentRole);
        this.router.navigate(['/location-picker']);
      }
      else {
        // Same role, check for saved URL redirect
        const savedUrl = sessionStorage.getItem('redirect_back_url');
        const currentUrl = this.router.url;

        // Only redirect if saved URL is different from current URL
        if (savedUrl && savedUrl !== '/' && !savedUrl.includes('location-picker') && savedUrl !== currentUrl) {
          sessionStorage.removeItem('redirect_back_url');
          this.router.navigateByUrl(savedUrl);
        } else if (!savedUrl || savedUrl === '/' || savedUrl.includes('location-picker')) {
          // No saved URL or it's a public route - go to location picker if needed
          if (!JSON.parse(sessionStorage.getItem('selectedFactory')) || !JSON.parse(sessionStorage.getItem('selectedCluster')) ||
            !JSON.parse(localStorage.getItem('savedClusterList'))) {
            this.router.navigate(['/location-picker']);
          }
        } else {
        }
      }

      // Check if factory/cluster selection is missing (but don't override navigation above)
      if (!JSON.parse(sessionStorage.getItem('selectedFactory')) || !JSON.parse(sessionStorage.getItem('selectedCluster')) ||
        !JSON.parse(localStorage.getItem('savedClusterList'))) {
        // Only navigate to location-picker if we haven't already navigated elsewhere
        const finalUrl = this.router.url;
        if (!finalUrl.includes('/location-picker') && !this.router.url.includes('/clusters/')) {
          this.router.navigate(['/location-picker']);
        }
      }
    }

    setTimeout(() => {
      this.getDfosLine();
    }, 1000);
  }

  public getDfosLine(): void {
    const dfos$ = this.requester.getLines("dfos");
    if (!dfos$) {
      return;
    }
    dfos$.subscribe((resp: any) => {
      if (resp && resp.status === "success" && resp.data.length > 0) {
        localStorage.setItem('dfosLines', JSON.stringify(resp));
      } else {
        localStorage.setItem('dfosLines', JSON.stringify({ "status": null, "data": [] }));
      }
    }, (err) => {
      // console.log(err);
    });
  }
}
